import React, { useEffect, useState, Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";

// Removed Material-UI imports and react-window to use only Tailwind

import { ResourceLoading } from "../ResourceLoading";
import { ResourceEmpty } from "../ResourceEmpty";
import { useTranslation } from "react-i18next";
import { ArrowLeftIcon, PlusIcon } from "@heroicons/react/24/solid";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { StringLimit } from "../../Utils";
import { ResourceError } from "../ResourceError";
import { useAutofocusOnOpen } from "../useAutofocusOnOpen";

export default function PMSimpleSelectorModalV2(props) {
  const [search, setSearch] = useState("");
  const [isSearchInitialized, setIsSearchInitialized] = useState(false);

  const {
    items,
    loading,
    label,
    createView,
    onClose,
    searchQuery,
    searchEnabled,
    error,
    zIndex,
    filterButtons,
    secondaryCharLimit = 35,
  } = props;

  const [inCreateView, setInCreateView] = useState(false);
  const searchInputRef = useAutofocusOnOpen(props.open && searchEnabled && !inCreateView);

  const { t } = useTranslation();

  const handleSelect = (item) => {
    props.callback({
      selected: { item },
    });
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setSearch(e.target.value);
    props.query(e.target.value);
    // Mark that user has modified the search
    setIsSearchInitialized(true);
  };

  const handleClose = () => {
    props.onClose();
  };

  // Initialize search from searchQuery prop only when modal opens for the first time
  useEffect(() => {
    if (props.open && !isSearchInitialized) {
      // Only initialize from searchQuery if it's provided and we haven't initialized yet
      if (searchQuery !== undefined && searchQuery !== null && searchQuery !== "") {
        setSearch(searchQuery);
        props.query(searchQuery);
        setIsSearchInitialized(true);
      }
    } else if (!props.open) {
      // Reset when modal closes
      setSearch("");
      setIsSearchInitialized(false);
    }
  }, [props.open]); // Only depend on open

  // Reset create view when modal closes
  useEffect(() => {
    if (!props.open) {
      setInCreateView(false);
    }
  }, [props.open]);

  // Removed renderItems function - using direct Tailwind rendering instead

  const renderListV2 = () => {
    if (loading) return <ResourceLoading />;
    if (error) return <ResourceError errors={error} />;

    return (
      <>
        <div>
          {items?.length > 0 ? (
            <div className="h-full overflow-y-auto">
              {items?.map((item) => (
                <div
                  key={item.id}
                  className="hover:border-l-4 hover:border-indigo-500 hover:bg-indigo-100 cursor-pointer px-4 py-3 border-b border-gray-200"
                  onClick={() => handleSelect(item)}
                >
                  <div className="text-sm font-medium text-gray-900">
                    <StringLimit text={item?.primary} limit={30} />
                  </div>
                  {item?.secondary && (
                    <div className="text-sm text-gray-500 mt-1">
                      <StringLimit text={item?.secondary} limit={secondaryCharLimit} />
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="mt-10 flex justify-center">
              {createView ? (
                <button
                  onClick={() => setInCreateView(!inCreateView)}
                  type="button"
                  className="bg-transparent hover:bg-green-500 text-green-700 font-semibold hover:text-white py-2 px-4 border border-green-500 hover:border-transparent rounded-2xl"
                >
                  {t("Create")} {label}
                </button>
              ) : (
                <ResourceEmpty />
              )}
            </div>
          )}
        </div>
      </>
    );
  };

  return (
    <>
      <Transition appear show={props.open} as={Fragment}>
        <Dialog as="div" className="relative" style={{ zIndex: zIndex ?? 2500 }} onClose={handleClose}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-2 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-lg transform overflow-hidden rounded-2xl bg-white p-4 text-left align-middle shadow-xl transition-all flex flex-col h-auto max-h-[calc(100dvh-1rem)] sm:h-[720px] sm:max-h-[80vh]">
                  <div className="flex justify-between">
                    <Dialog.Title
                      as="h3"
                      className="text-lg font-medium leading-6 text-gray-900 py-2 px-4 pt-2"
                    >
                      {t("Choose")} {label}
                    </Dialog.Title>

                    <div>
                      {createView && (
                        <button
                          onClick={() => setInCreateView(!inCreateView)}
                          type="button"
                          className="bg-transparent hover:bg-green-300 text-green-500 font-semibold hover:text-white py-2 px-4 border border-green-300 hover:border-transparent rounded"
                        >
                          {!inCreateView ? (
                            <PlusIcon className="h-5 w-5" />
                          ) : (
                            <ArrowLeftIcon className="h-5 w-5" />
                          )}
                        </button>
                      )}
                      <button
                        onClick={onClose}
                        type="button"
                        className="bg-transparent hover:bg-gray-300 text-gray-500 font-semibold hover:text-white py-2 px-4 rounded"
                      >
                        <XMarkIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                  {filterButtons && filterButtons.length > 0 && (
                    <div className="mt-5 flex gap-2 flex-wrap">
                      {filterButtons.map((button, index) => (
                        <button
                          key={index}
                          type="button"
                          onClick={button.onClick}
                          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                            button.active
                              ? "bg-blue-600 text-white"
                              : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                          }`}
                        >
                          {button.label}
                        </button>
                      ))}
                    </div>
                  )}
                  {searchEnabled && (
                    <div className="mt-5">
                      <input
                        ref={searchInputRef}
                        type="text"
                        name="search"
                        className="w-full px-4 py-2 border border-gray-400 focus:outline-none focus:border-gray-500 mb-2 rounded-2xl"
                        placeholder={"Søk etter " + label + "..."}
                        onChange={(e) => handleSearch(e)}
                        value={search}
                      />
                    </div>
                  )}
                  <div className="flex-1 min-h-0 overflow-auto">
                    {!inCreateView ? (
                      renderListV2()
                    ) : (
                      <div className="py-2 px-4">{createView && createView()}</div>
                    )}
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
}
